<script setup lang="ts">
import { nextTick, reactive, watch } from 'vue';
import CInput from '@/components/shared/CInput';
import CButton from '@/components/shared/CButton';
import { getSetSize, getSharedRepeat } from '@/utils/patternEffects';
import type { PatternElement, SharedRepeatChange } from '@/typings/customilyElements/patternElements';

const props = defineProps<{
    // null too while the selection isn't all pattern origins: `| null` compiles to a runtime type Vue 2.7 rejects
    origins?: PatternElement[];
}>();

const emit = defineEmits<{
    (e: 'pattern-change', change: SharedRepeatChange): void;
    (e: 'refresh-fill'): void;
    (e: 'remove-pattern'): void;
}>();

type Field = 'spacingX' | 'spacingY' | 'offsetX' | 'offsetY';

// Strings: CInput shows a 0 as empty, which would read as Mixed
const model = reactive<Record<Field, string>>({ spacingX: '', spacingY: '', offsetX: '', offsetY: '' });
// What the patterns have: the model lags behind while a "-" is being typed
const applied: Record<Field, string> = { ...model };
// The selection taken as one element: same % as a single pattern, measured on the set
const setSize = reactive({ width: 0, height: 0 });

// `|| 0` turns a -0 into 0
const toPercent = (value: number | null, size: number) =>
    value === null || !size ? '' : String(Math.round((value / size) * 100) || 0);

const initModel = () => {
    const origins = props.origins || [];
    Object.assign(setSize, origins.length ? getSetSize(origins) : { width: 0, height: 0 });
    const shared = getSharedRepeat(origins);
    model.spacingX = toPercent(shared.repeatX === null ? null : shared.repeatX - setSize.width, setSize.width);
    model.spacingY = toPercent(shared.repeatY === null ? null : shared.repeatY - setSize.height, setSize.height);
    model.offsetX = toPercent(shared.shiftX, setSize.width);
    model.offsetY = toPercent(shared.shiftY, setSize.height);
    Object.assign(applied, model);
};

watch(() => props.origins, initModel, { immediate: true });

// A cleared number CInput emits 0, as in PatternProperties: it applies 0% to every pattern
const onInput = (field: Field, value: unknown) => {
    const percent = Number(value);
    if (Number.isNaN(percent)) return;
    // Measured now: the transform panel scales the same selection, so origins (what the watch sees) stay the same
    if (props.origins?.length) Object.assign(setSize, getSetSize(props.origins));
    const fraction = percent / 100;
    const changes: Record<Field, () => SharedRepeatChange> = {
        spacingX: () => ({ repeatX: setSize.width * (1 + fraction) }),
        spacingY: () => ({ repeatY: setSize.height * (1 + fraction) }),
        offsetX: () => ({ shiftX: setSize.width * fraction }),
        offsetY: () => ({ shiftY: setSize.height * fraction })
    };
    const change = changes[field]();
    if ((change.repeatX ?? 1) <= 0 || (change.repeatY ?? 1) <= 0) {
        // Copies at no distance or less: shows what's applied again. Next tick, or the input keeps what was typed
        model[field] = String(percent);
        nextTick(() => (model[field] = applied[field]));
        return;
    }
    applied[field] = String(percent);
    // A lone "-" (or a cleared field) also comes as 0: written back, the input would replace that "-" with a "0"
    if (percent !== 0) model[field] = String(percent);
    emit('pattern-change', change);
};

// Left empty or with a lone "-", it shows the 0 it applied instead of reading as Mixed
const onBlur = (field: Field) => {
    if (applied[field] === '') return;
    model[field] = '';
    nextTick(() => (model[field] = applied[field]));
};
</script>

<template>
    <div class="shared-pattern-wrapper">
        <div class="section-title">Spacing</div>
        <hr class="section-divider" />
        <div class="row">
            <div class="col-6">
                <c-input
                    :value="model.spacingX"
                    type="number"
                    label="Horizontal (%)"
                    placeholder="Mixed"
                    :step="1"
                    @input="onInput('spacingX', $event)"
                    @blur="onBlur('spacingX')"
                />
            </div>
            <div class="col-6">
                <c-input
                    :value="model.spacingY"
                    type="number"
                    label="Vertical (%)"
                    placeholder="Mixed"
                    :step="1"
                    @input="onInput('spacingY', $event)"
                    @blur="onBlur('spacingY')"
                />
            </div>
        </div>

        <div class="section-title">Offset</div>
        <hr class="section-divider" />
        <div class="row">
            <div class="col-6">
                <c-input
                    :value="model.offsetX"
                    type="number"
                    label="Horizontal (%)"
                    placeholder="Mixed"
                    :step="1"
                    @input="onInput('offsetX', $event)"
                    @blur="onBlur('offsetX')"
                />
            </div>
            <div class="col-6">
                <c-input
                    :value="model.offsetY"
                    type="number"
                    label="Vertical (%)"
                    placeholder="Mixed"
                    :step="1"
                    @input="onInput('offsetY', $event)"
                    @blur="onBlur('offsetY')"
                />
            </div>
        </div>

        <div class="pattern-actions">
            <c-button
                class="w-50"
                variant="secondary"
                text="Refresh to Fill Page"
                :icon="['far', 'arrow-rotate-left']"
                @click="emit('refresh-fill')"
            />
            <c-button
                class="w-50"
                variant="secondary"
                text="Remove"
                :icon="['far', 'trash']"
                @click="emit('remove-pattern')"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.shared-pattern-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;

    .section-title {
        font-weight: 400;
        font-size: 0.875rem;
    }

    .section-divider {
        margin: 0;
    }

    .pattern-actions {
        display: flex;
        justify-content: space-between;
        gap: 0.5rem;
        margin-top: 0.5rem;
    }
}
</style>
